import {
  CAMERA_PRESENTER,
  RESET_USER_VIRTUAL_BACKGROUND,
  PASSED,
  VENDOR_AGORA
} from '@studio/constants';
import {
  GenericCameraSourceSettings,
  AgoraCameraPreview
} from '@shoflo/polymer';
import React from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { GENERIC_AUDIO_VIDEO_SETTINGS_FORM } from 'constants/ModalTypes';
import { ModalBody } from 'reactstrap';
import {
  clearModal,
  setAudioInputDevice,
  setAudioOutputDevice,
  setUserBackground,
  setVideoInputDevice,
  setTransformedVideoInputs,
  uploadUserVirtualBackground,
  deleteUserVirtualBackground,
  addToAttendeeTiles,
  showNotification
} from '@shoflo/studio-redux/actions';
import { updatePresenterSettings } from 'actions/Broadcast';
import { StyledModal } from './SourceSettings.styled';
import setGenericAudioInputDevice from '@shoflo/studio-redux/actions/meeting/setGenericAudioInputDevice';
import {
  clearVirtualBackground as agoraClearVirtualBackground,
  getVideoInput as agoraGetVideoInput,
  initializeVirtualBackground as agoraInitializeVirtualBackground
} from '@cvent/studio-agora/virtualBackgrounds/virtualBackground';

import { switchVideoInput as agoraSwitchVideoInput } from '@cvent/studio-agora/video/videoHelpers';
import { switchAudioInput as agoraSwitchAudioInput } from '@cvent/studio-agora/audio/audioHelper';

const VENDOR_CONFIG_MAP = {
  [VENDOR_AGORA]: {
    cameraPreviewElement: AgoraCameraPreview,
    clearVirtualBackground: agoraClearVirtualBackground,
    initializeBackgroundFilter: agoraInitializeVirtualBackground,
    getVideoInput: agoraGetVideoInput,
    switchVideoInput: agoraSwitchVideoInput,
    switchAudioInput: agoraSwitchAudioInput
  }
};

const GenericSourceSettings = () => {
  const modal = useSelector(state => state.modal);
  const client = useSelector(state => state.meeting.client, shallowEqual);
  const attendeeTiles = useSelector(state => state.meeting.attendeeTiles);
  const attendeeId = useSelector(state => state.meeting.attendeeId);
  const videoInputDevice = useSelector(state => state.videoInputDevice);
  const audioInputDevice = useSelector(state => state.audioInputDevice);
  const audioOutputDevice = useSelector(state => state.audioOutputDevice);
  const userBackground = useSelector(state => state.userBackground);
  const transformedVideoInputs = useSelector(
    state => state.transformedVideoInputs
  );
  const user = useSelector(state => state.user, shallowEqual);
  const cameraStatus = useSelector(state => state.readinessCheck.camera);

  const dispatch = useDispatch();

  const currentVideoTrack = attendeeTiles[attendeeId];

  const isCameraAvailable = cameraStatus === PASSED;

  const isPresenterEditForm = modal?.type === GENERIC_AUDIO_VIDEO_SETTINGS_FORM;

  const vendor = modal?.initialValue?.vendor;

  if (!isPresenterEditForm || !modal?.initialValue || !vendor) return null;

  const vendorConfig = VENDOR_CONFIG_MAP[vendor];

  if (!vendorConfig) return null;

  const {
    cameraPreviewElement,
    clearVirtualBackground,
    initializeBackgroundFilter,
    getVideoInput,
    switchVideoInput,
    switchAudioInput
  } = vendorConfig;

  const closeModal = () => dispatch(clearModal(modal.type));

  const showThisModal =
    isPresenterEditForm && modal.initialValue?.type === CAMERA_PRESENTER;

  const onCancel = async initialDataPayload => {
    const { transformedVideoInputs, videoInput, videoTrack } =
      initialDataPayload;

    dispatch(setVideoInputDevice(videoInput));
    dispatch(setTransformedVideoInputs(transformedVideoInputs));

    if (videoTrack) {
      dispatch(addToAttendeeTiles(attendeeId, videoTrack));
    }

    closeModal();
  };

  const onSubmit = data => {
    const {
      audioInputDeviceId,
      audioOutputDeviceId,
      title,
      description,
      userBackground,
      transformedVideoInputs,
      videoInput,
      videoTrack
    } = data;

    if (videoTrack) {
      dispatch(addToAttendeeTiles(attendeeId, videoTrack));
    }

    dispatch(setGenericAudioInputDevice(audioInputDeviceId));
    dispatch(setAudioInputDevice(audioInputDeviceId));
    dispatch(setVideoInputDevice(videoInput));
    dispatch(setAudioOutputDevice(audioOutputDeviceId));
    dispatch(setUserBackground(userBackground));
    dispatch(setTransformedVideoInputs(transformedVideoInputs));

    dispatch(
      updatePresenterSettings(modal.initialValue, {
        source_title: title,
        description
      })
    );
    closeModal();
  };

  const { title, description = '' } = modal.initialValue;
  const handleDeleteUserVirtualBackground = () => {
    dispatch(deleteUserVirtualBackground());
  };
  const handleUploadUserVirtualBackground = file =>
    dispatch(uploadUserVirtualBackground(file));
  const handleResetUserVirtualBackground = () =>
    dispatch({ type: RESET_USER_VIRTUAL_BACKGROUND });

  const deviceActions = {
    addToAttendeeTiles,
    handleVideoInputChanged: setVideoInputDevice,
    handleAudioInputChanged: setGenericAudioInputDevice,
    showNotification
  };

  return (
    <StyledModal isOpen={showThisModal} backdrop="static" keyboard="false">
      <ModalBody className="p-1">
        <GenericCameraSourceSettings
          title={title}
          description={description}
          onSubmit={onSubmit}
          onCancel={onCancel}
          audioInputDeviceId={audioInputDevice}
          audioOutputDeviceId={audioOutputDevice}
          videoInputDeviceId={videoInputDevice?.device ?? videoInputDevice}
          isCameraAvailable={isCameraAvailable}
          userBackground={userBackground}
          transformedVideoInputs={transformedVideoInputs}
          uploadUserVirtualBackground={handleUploadUserVirtualBackground}
          deleteUserVirtualBackground={handleDeleteUserVirtualBackground}
          resetUserVirtualBackground={handleResetUserVirtualBackground}
          user={user}
          CameraPreviewElement={cameraPreviewElement}
          clearVirtualBackground={clearVirtualBackground}
          initializeBackgroundFilter={initializeBackgroundFilter}
          getVideoInput={getVideoInput}
          switchVideoInput={switchVideoInput}
          switchAudioInput={switchAudioInput}
          currentVideoTrack={currentVideoTrack}
          client={client}
          dispatch={dispatch}
          deviceActions={deviceActions}
        />
      </ModalBody>
    </StyledModal>
  );
};

export { GenericSourceSettings };
